import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Query keys for consistent cache management
export const queryKeys = {
  // Auth
  profile: ['profile'],

  // Dashboard
  dashboardOverview: ['dashboard', 'overview'],
  dashboardMetrics: ['dashboard', 'metrics'],
  dashboardAlerts: ['dashboard', 'alerts'],
  dashboardBird: (id: number) => ['dashboard', 'bird', id],
  dashboardDaily: ['dashboard', 'daily'],

  // Birds
  birds: ['birds'],
  birdsList: (params?: any) => ['birds', 'list', params],
  bird: (id: number) => ['birds', id],
  birdsStats: ['birds', 'stats'],
  birdsQuantities: ['birds', 'quantities'],
  birdLogs: (id: number) => ['birds', id, 'logs'],
  birdTypes: ['birds', 'types'],
  birdBlocks: (id: number) => ['birds', id, 'blocks'],
  birdBlocksFeed: (id: number) => ['birds', id, 'blocks', 'feed'],

  // Bird Batches
  birdBatches: ['bird-batches'],
  birdBatchesList: (params?: any) => ['bird-batches', 'list', params],
  birdBatch: (id: number) => ['bird-batches', id],
  birdBatchEmployees: (batchId: number) => ['bird-batches', batchId, 'employees'],

  // Bird Procurements
  birdProcurements: ['bird-procurements'],
  birdProcurementsList: (params?: any) => ['bird-procurements', 'list', params],
  birdProcurement: (id: number) => ['bird-procurements', id],
  birdProcurementsStats: ['bird-procurements', 'stats'],

  // Egg Production
  eggProductions: ['egg-productions'],
  eggProductionsList: (params?: any) => ['egg-productions', 'list', params],
  eggProduction: (id: number) => ['egg-productions', id],
  eggProductionsStats: ['egg-productions', 'stats'],
  eggProductionsTrends: (params?: any) => ['egg-productions', 'trends', params],
  eggProductionsTopPerformance: ['egg-productions', 'top-performance'],

  // Egg Sales
  eggSales: ['egg-sales'],
  eggSalesList: (params?: any) => ['egg-sales', 'list', params],
  eggSale: (id: number) => ['egg-sales', id],
  eggSalesStats: ['egg-sales', 'stats'],
  eggSalesPending: ['egg-sales', 'pending'],
  eggSalesTrends: (params?: any) => ['egg-sales', 'trends', params],

  // Egg Stock
  eggStock: ['egg-stock'],
  eggStockSummary: ['egg-stock', 'summary'],
  eggStockAlerts: ['egg-stock', 'alerts'],

  // Egg Prices
  eggPrices: ['egg-prices'],
  eggPricesCurrent: ['egg-prices', 'current'],
  eggPricesHistory: (birdId: number) => ['egg-prices', 'history', birdId],

  // Customers
  customers: ['customers'],
  customersList: (params?: any) => ['customers', 'list', params],
  customer: (id: number) => ['customers', id],
  customersStats: ['customers', 'stats'],
  customersOutstanding: ['customers', 'outstanding'],
  customersLoyal: ['customers', 'loyal'],
  customerPurchaseHistory: (id: number) => ['customers', id, 'purchase-history'],

  // Financial Cycles
  financialCycles: ['financial-cycles'],
  financialCyclesCurrent: ['financial-cycles', 'current'],
  financialCyclesSummary: ['financial-cycles', 'summary'],
  financialCyclesTrends: ['financial-cycles', 'trends'],
  financialCycle: (id: number) => ['financial-cycles', id],

  // Expenditures
  expenditures: ['expenditures'],
  expendituresList: (params?: any) => ['expenditures', 'list', params],
  expenditure: (id: number) => ['expenditures', id],
  expendituresStats: ['expenditures', 'stats'],
  expendituresPending: ['expenditures', 'pending'],
  expendituresTrends: (params?: any) => ['expenditures', 'trends', params],

  // Expenditure Categories
  expenditureCategories: ['expenditure-categories'],
  expenditureCategoriesStats: ['expenditure-categories', 'stats'],

  // Mortalities
  mortalities: ['mortalities'],
  mortalitiesList: (params?: any) => ['mortalities', 'list', params],
  mortality: (id: number) => ['mortality', id],
  mortalitiesStats: (params?: any) => ['mortalities', 'stats', params],
  mortalitiesTrends: (params?: any) => ['mortalities', 'trends', params],
  // Decommissions
  decommissions: {
    all: ['decommissions'],
    list: (params?: any) => ['decommissions', 'list', params],
    detail: (id: number) => ['decommissions', id],
    stats: () => ['decommissions', 'stats'],
    trends: (params?: any) => ['decommissions', 'trends', params],
  },
  // Feed
  feedDashboard: ['feed', 'dashboard'],
  feedTypes: ['feed', 'types'],
  feedBrands: ['feed', 'brands'],
  feedStock: ['feed', 'stock'],
  feedStockAlerts: ['feed', 'stock-alerts'],
  feedProcurements: ['feed', 'procurements'],
  feedAssignments: ['feed', 'assignments'],
  feedTrends: ['feed', 'trends'],
  feedCostAnalysis: ['feed', 'cost-analysis'],

  // Requests
  requests: ['requests'],
  requestsList: (params?: any) => ['requests', 'list', params],
  request: (id: number) => ['requests', id],
  requestsStats: ['requests', 'stats'],

  // Misc
  miscUsers: ['misc', 'users'],
  miscProcurements: ['misc', 'procurements'],
  miscCustomers: ['misc', 'customers'],
  miscLayingProcurements: ['misc', 'laying-procurements'],

  //suppliers
  birdProcurementsSuppliers: ['bird-procurements', 'suppliers'],

  // Preventive Health
  ph: {
    programmes: ['ph', 'programmes'],
    programme: (id: number) => ['ph', 'programme', id] as const,
    batchSchedule: (batchId: number, params?: any) => ['ph', 'schedule', batchId, params] as const,
    batchUpcoming: (batchId: number, params?: any) => ['ph', 'upcoming', batchId, params] as const,
    upcomingTasks: (params?: any) => ['ph', 'tasks', 'upcoming', params] as const,
    todayTasks: (params?: any) => ['ph', 'tasks', 'today', params] as const,
    alerts: (params?: any) => ['ph', 'alerts', params] as const,
    reportWithdrawal: (params?: any) => ['ph', 'report', 'withdrawal', params] as const,
    reportCompliance: (params?: any) => ['ph', 'report', 'compliance', params] as const,
    reportTreatments: (params?: any) => ['ph', 'report', 'treatments', params] as const,
    products: (params?: any) => ['ph', 'products', params] as const,
  },
} as const;
